/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createDisposer, type IntervalId, logDebug, type Unsubscribe } from '@amazon/vinyl-util'
import type { Player } from '@/playback/api'
import { getDeviceConfig } from '@/device/deviceConfig'
import { type KeyBinding, matchesKey } from '@/device/input/KeyMap'
import { windowEvents } from '@/util/interaction'
import type { PlaylistController } from '@/playback/PlaylistController'
import { ctx } from '@/util/context'
import { guardedPrevious, guardedSkipForward } from '@/playback/skipGuard'
import { markUserSkip } from '@/playback/metrics/userSkipSignal'

const { target, errorHandler } = ctx('mediaKeyBinding')

export interface MediaKeyBindingDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
}

export function initializeMediaKeyBindings(deps: MediaKeyBindingDeps): Unsubscribe {
    const disposer = createDisposer()
    const { add, dispose } = disposer
    const { playerPromise, playlistController } = deps

    const KEY_HOLD_THRESHOLD = 0.5 // Time (in seconds) to consider a key as "held"

    playerPromise
        .then((player) => {
            if (disposer.disposed) return
            logDebug(target, 'initializing media key bindings')

            const { keyMap } = getDeviceConfig()

            add(
                keyRepeatHandler({
                    key: keyMap.rewind,
                    onRepeat: (elapsed, _repeat) => {
                        if (elapsed > KEY_HOLD_THRESHOLD) {
                            player.seekTo(player.currentTime$.value - 10).catch(errorHandler)
                        }
                    },
                    onEnd: (elapsed) => {
                        if (elapsed <= KEY_HOLD_THRESHOLD) {
                            // Tap behavior: If currentTime > 3, seek to 0
                            // (always allowed); otherwise go to previous
                            // track, gated on back-nav capability so a
                            // capped / ad entry surfaces the upsell.
                            if (player.currentTime$.value > 3) {
                                player.seekTo(0).catch(errorHandler)
                            } else if (guardedPrevious(playlistController)) {
                                markUserSkip('previous')
                                playlistController.skipPrevious().catch(errorHandler)
                            }
                        }
                    },
                })
            )

            add(
                keyRepeatHandler({
                    key: keyMap.fastForward,
                    onRepeat: (elapsed, _repeat) => {
                        if (elapsed > KEY_HOLD_THRESHOLD) {
                            player.seekTo(player.currentTime$.value + 10).catch(errorHandler)
                        }
                    },
                    onEnd: (elapsed) => {
                        if (elapsed <= KEY_HOLD_THRESHOLD) {
                            // Tap behavior: Next track. Gate on skip
                            // capability so a capped / ad entry
                            // surfaces the upsell instead of skipping.
                            if (!guardedSkipForward(playlistController)) return
                            markUserSkip('next')
                            playlistController.skipNext().catch(errorHandler)
                        }
                    },
                })
            )

            add(
                windowEvents.on('keydown', (e: KeyboardEvent) => {
                    let handled = true

                    if (matchesKey(e, keyMap.playPause)) {
                        if (player.paused$.value) player.play().catch(errorHandler)
                        else player.pause()
                    } else {
                        handled = false
                    }

                    if (handled) e.preventDefault()
                })
            )
        })
        .catch(errorHandler)
    return dispose
}

const KEY_REPEAT_INTERVAL_SECONDS = 0.5

function keyRepeatHandler({
    key,
    onRepeat,
    onEnd,
}: {
    key: KeyBinding
    onRepeat: { (elapsed: number, repeat: number): void }
    onEnd: { (elapsed: number): void }
}): Unsubscribe {
    const { add, dispose } = createDisposer()
    let c = 0
    let start = 0
    let intervalId: IntervalId | null = null

    const clear = () => {
        if (intervalId != null) {
            clearInterval(intervalId)
            intervalId = null
        }
    }
    add(clear)
    add(
        windowEvents.on('keydown', (e: KeyboardEvent) => {
            if (intervalId != null) return
            if (matchesKey(e, key)) {
                start = Date.now()
                c = 0
                intervalId = setInterval(() => {
                    const elapsed = (Date.now() - start) / 1000
                    onRepeat(elapsed, ++c)
                }, KEY_REPEAT_INTERVAL_SECONDS * 1000)
                e.preventDefault()
            }
        })
    )

    add(
        windowEvents.on('keyup', (e: KeyboardEvent) => {
            if (matchesKey(e, key)) {
                clear()
                onEnd((Date.now() - start) / 1000)
                e.preventDefault()
            }
        })
    )

    return dispose
}
